import React from 'react'
import Head from 'next/head'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { getDefaultCentreMetadata } from '@scentregroup/shared/helpers/get-default-centre-metadata'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import { DateTime } from '@scentregroup/shared/helpers/date-helpers'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../types/next'
import { makeApolloClient } from '../../lib/apolloClient'
import { getCentrePageTemplate } from '../../lib/page-templates/fetching'
import { SGPageTemplateWithAdZones } from '../../../../src/types/page-templates'
import { OpenGraph } from '../../lib/open-graph'
import { ContentDetail, getContentDetail } from '../../lib/get-content-detail'
import { ParkingContentDetail } from '../../components/page-templates/content-detail/parking'
import {
  CentreSlug,
  DateString,
  getTradingHours,
} from '../../lib/get-trading-hours'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'

export async function getServerSideProps(
  ctx: GetServerSidePropsContext<{
    centre: string
    id: string
    slug: string
  }>
): Promise<GetServerSidePropsResult<ICentreParkingPageProps>> {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)

  const centreSlug = ctx.params?.centre
  if (!centreSlug) {
    return {
      notFound: true,
    }
  }

  const apollo = makeApolloClient()
  const centre = await getCentreDetails(apollo, { slug: centreSlug, country })
  if (!centre) {
    return {
      notFound: true,
    }
  }

  const [template, parking, headerStaticData, topStripProps] =
    await Promise.all([
      getCentrePageTemplate(apollo, centreSlug, 'parking'),
      getContentDetail(apollo, 'parking', { id: centre.sys.id }),
      fetchCentreStaticData(apollo, {
        country: localeToCountry(ctx.locale),
        centre,
      }),
      getTopStripProps(apollo, country, centreSlug),
    ])

  if (!template || !parking) {
    return {
      notFound: true,
    }
  }

  const startDate = DateTime.moment()
    .tz(centre.timeZone)
    .startOf('day')
    .format('YYYY-MM-DD')
    .toString() as DateString

  const centreTradingHours = await getTradingHours(
    apollo,
    centreSlug as CentreSlug,
    undefined,
    startDate
  )

  return {
    props: {
      country,
      topStripProps,
      centre,
      parking,
      template,
      headerStaticData,
      centreTradingHours,
    },
  }
}

export interface ICentreParkingPageProps {
  country: 'Australia' | 'New Zealand'
  topStripProps: ITopStripProps | null
  centre: CentreDetails
  parking: ContentDetail['parking']
  template: SGPageTemplateWithAdZones
  headerStaticData: CentreStaticData
  centreTradingHours: TradingHourDay[]
}

export default function CentreParkingPage({
  country,
  topStripProps,
  centre,
  parking,
  template,
  headerStaticData,
  centreTradingHours,
}: ICentreParkingPageProps): JSX.Element {
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const meta = getDefaultCentreMetadata({
    centre,
    canonical,
  })

  const metaData = {
    title: `Westfield ${centre.title} Parking | Parking Rates & Fees`,
    description: `Westfield ${centre.title} offers you secure car parking. Check parking information including entrances, hourly rates and more at Westfield ${centre.title}`,
  }

  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)

  const parkingCentreTradingHours = {
    features: centre.features,
    timeZone: centre.timeZone,
    tradingHours: centreTradingHours,
  }

  return (
    <>
      <SGLinkedData item={meta.schema} />
      <OpenGraph data={meta.openGraphData} />
      <Head>
        <title>{metaData.title}</title>
        <meta
          key="description"
          name="description"
          content={metaData.description}
        />
        <link key="canonical" rel="canonical" href={canonical} />
      </Head>
      <Layout
        country={country}
        topStrip={topStripProps}
        centre={{
          ...centre,
          contactLinks,
        }}
        header={{
          variant: 'centre',
          data: headerStaticData,
          logoText: centre.title,
        }}
        metaData={metaData}
      >
        <ParkingContentDetail
          centre={{
            ...centre,
            contactLinks,
          }}
          content={parking}
          template={template}
          centreTradingHours={parkingCentreTradingHours}
        />
      </Layout>
    </>
  )
}
